import { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    getAllMaterials,
    setActiveMaterial,
    startEditingMaterial,
    startDeletingMaterial,
    selectAllMaterials,
    getMaterialsDownloadState,
} from '../../store/slices/materialsSlice';
import { getTimezone } from '../../store/slices/loginSlice';
import { useTranslation } from 'react-i18next';
import usePermission from '../../hooks/usePermission';
import { LinearProgress } from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import DataGridStyle from '../../styles/DataGridStyle';
import TableActionsMenu from '../../components/table/TableActionsMenu';
import TableActionEdit from '../../components/table/TableActionEdit';
import TableActionDelete from '../../components/table/TableActionDelete';
import NoRowsOverlay from '../../assets/overlays/NoRowsOverlay';
import NoPermissionsOverlay from '../../assets/overlays/NoPermissionsOverlay';
import moment from 'moment-timezone';


function GridMaterials() {

    const dispatch = useDispatch();
    const { t } = useTranslation();
    const isMounted = useRef(false);

    const writeAccess = usePermission(['materials_write'])
    const readAccess = usePermission(['materials_read'])

    const rows = useSelector(selectAllMaterials);
    const downloadState = useSelector(getMaterialsDownloadState);
    const timezone = useSelector(getTimezone)

    const [anchorEl, setAnchorEl] = useState(null);


    const columns = [
        {
            field: 'material_id',
            headerName: '#',
            width: 40,
            sortable: false,
            filterable: false,
            hideable: false,
            columnMenu: false,
            disableColumnMenu: true,
            valueGetter: (params) => params.api.getRowIndexRelativeToVisibleRows(params.row.material_id) + 1,
        },
        {
            field: 'material_name',
            headerName: t('label.material_name'),
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'material_number',
            headerName: t('label.material_number'),
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'unit',
            headerName: t('label.unit'),
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'description',
            headerName: t('label.description'),
            minWidth: 150,
            flex: 1,
        },
        {
            field: 'created',
            headerName: t('label.created'),
            minWidth: 150,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (params) => moment.utc(params.row.created).tz(timezone).format('DD-MM-YYYY, HH:mm')
        },
        {
            field: 'updated',
            headerName: t('label.updated'),
            minWidth: 150,
            flex: 1,
            headerAlign: 'center',
            align: 'center',
            valueGetter: (params) => moment.utc(params.row.updated).tz(timezone).format('DD-MM-YYYY, HH:mm')
        },
        {
            field: 'actions',
            headerName: t('label.actions'),
            minWidth: 100,
            sortable: false,
            filterable: false,
            hideable: false,
            headerAlign: 'center',
            align: 'center',
            disableColumnMenu: true,
            renderCell: () => (
                <TableActionsMenu
                    onClick={handleMenuClick}
                    permission={writeAccess}
                    anchorEl={anchorEl}
                    setAnchorEl={setAnchorEl}

                >
                    <TableActionEdit onClick={handleEditClick} />
                    <TableActionDelete onClick={handleDeleteClick} />
                </TableActionsMenu>
            )
        },
    ];

    /* --------- GET ---------------------------------------------------------------------*/

    useEffect(() => {
        if (isMounted.current) return;
        /* eslint-disable */
        dispatch(getAllMaterials());

        isMounted.current = true;
    }, []);

    /* -----------------------------------------------------------------------------------*/

    const handleRowClick = (params) => {
        dispatch(setActiveMaterial(params.row));
    };

    const handleMenuClick = (e) => {
        setAnchorEl(e.currentTarget);
    };

    const handleEditClick = () => {
        dispatch(startEditingMaterial());
        setAnchorEl(null);
    };

    const handleDeleteClick = () => {
        dispatch(startDeletingMaterial());
        setAnchorEl(null);
    };


    return (
        <DataGridStyle>
            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(row) => row.material_id}
                loading={downloadState.status === 'loading'}
                onRowClick={(params) => handleRowClick(params)}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            description: false,
                            created: false,
                            updated: false,
                        },
                    },
                    sorting: { sortModel: [{ field: 'material_name', sort: 'asc' }] },
                    pagination: { paginationModel: { pageSize: 100 } },
                    filter: {
                        filterModel: {
                            items: [],
                            quickFilterExcludeHiddenColumns: true,
                        },
                    },
                }}
                slots={{
                    toolbar: GridToolbar,
                    loadingOverlay: LinearProgress,
                    noRowsOverlay: readAccess ? NoRowsOverlay : NoPermissionsOverlay,
                }}
                slotProps={{
                    toolbar: {
                        showQuickFilter: true,
                    },
                }}
            />
        </DataGridStyle>
    );
};

export default GridMaterials;